/**
 * Element commands — the ONLY write path for elements. Each wraps its mutations in one
 * Yjs transaction with the session origin (one command = one undo step).
 */
import { carriedByCasework, reseatStudioFixtures } from './caseworkFollow'
import { setOpeningLevel } from './hosting'
import * as Y from 'yjs'
import type {
  CategoryId, ElementId, LevelId, ParamValue, Path2D, PhaseAreaEntry, PhaseId, PhaseSplitEntry, PhaseSplitId, Pt2, TypeId,
} from '../types'
import { newElementId, newNodeId, newPhaseSplitId } from '../ids'
import {
  getElement, getType, listElements, listPhases, listTypes, maps, recordToYMap, resolveElement, resolveAllElements, transact, type ModelDoc,
} from '../doc'
import { setLocationLine } from './topology'
import { CHAIR_LINK_KEYS, chairsOfTable, dropCarriedChairs, isDiningTable, setDiningChairCount } from './diningChairs'
import { centerlineOffsetFt, type LocationLine } from '../locationLine'
import { polylinePointAt, projectToPolyline } from '../pathOps'
import { flattenPathPts } from '../arc'
import { getCategory } from '../registry'
import { schemaParams } from '../params'
import { getEditPhase, normalizePhaseChanges, withChange, withEdgeChange, UNSET, PHASE_CHANGES_KEY } from '../phaseChanges'
import { isRoofEaveParam, eaveEditPhaseFor } from '../eavePhase'
import { hostedPhaseFields, phaseStatus } from '../phasing'
import { normalizeScope, migrateWallScope, scopeHasWork, SCOPE_KEY, type ElementScope, type ScopeVerb } from '../scope'
import { mirrorElement, mirrorHostedInPlace, rotateElement, stretchElementPath, translateElement, type MoveReport } from './geometryOps'
import { sizeSeriesEntries } from '../openingSize'
import { doorFollowsProjectHeight, normalizeOpeningStyle } from '../openingStyle'
import { FIXTURE_MOUNT_STANDARD_FT, WALL_FIXTURE_KINDS, CEILING_FIXTURE_KINDS } from '../categories/fixture/fixtureKinds'
import { ceilingAtPoint, objectAnchorPoint, surfaceHostAtPoint } from '../snap'
import { SURFACE_SITTING_OBJECT_KINDS } from '../categories/object/objectKinds'
import { applianceSizeOf } from '../categories/object/applianceSizes'

import {
  detachCopyIntoOwnRun, expandRuns, healRunAfterDelete, remapCopiedRuns, runIdOf, setRunBoxWidth,
} from './caseworkRun'

/**
 * OPENING CONFIGURATION GOVERNANCE round (2026-08-10): a brand-new door/window
 * instance with no width/height already supplied (no eyedropper/create-similar
 * pickup riding along — those already carry widthFt/heightFt forward since they're
 * ordinary transferable instance params, not positional) starts life at its TYPE's
 * own curated size — the first entry of `sizeSeries` — rather than the flat generic
 * instance-schema default (3'/6'-8" for every door regardless of a "Pocket 2'-8""
 * type's intent). A type with no curated series yet (brand-new, uncurated) falls
 * through to the schema default unchanged.
 */
function seededOpeningSize(model: ModelDoc, category: CategoryId, typeId: TypeId): Record<string, ParamValue> {
  if (category !== 'door' && category !== 'window') return {}
  const type = getType(model, typeId)
  const series = sizeSeriesEntries(type?.params.sizeSeries)
  const out: Record<string, ParamValue> = series.length ? { widthFt: series[0].widthFt, heightFt: series[0].heightFt } : {}
  // TYPICAL DOOR HEIGHTS (2026-10-08): a door is placed with NO height of its own, so it follows
  // the project's typical height for its wall (else the type's first size — openingStyle.ts)
  if (category === 'door' && doorFollowsProjectHeight(type?.params.operation)) delete out.heightFt
  // …and with the project's "line up window heads" on, a new window's head sits at the door height
  if (category === 'window' && normalizeOpeningStyle(maps(model).settings.get('openingStyle')).windowHeadsAlign) {
    out.sillMode = 'doorHead'
  }
  return out
}

/**
 * TYPICAL DOOR HEIGHTS: make the doors in interior (or exterior) walls that have a height of
 * their own follow the project's typical height instead. Returns how many changed.
 */
export function followProjectDoorHeights(model: ModelDoc, side: 'interior' | 'exterior', dryRun = false): number {
  const ids: ElementId[] = []
  for (const el of listElements(model)) {
    if (el.category !== 'door') continue
    const p = el.params as Record<string, ParamValue>
    if (p.heightFt === undefined || p.heightFt === null || p.heightFt === '') continue
    const r = resolveElement(model, el.id as ElementId)
    if (!r || !doorFollowsProjectHeight(r.typeParams.operation)) continue
    const host = el.hostId ? resolveElement(model, el.hostId as ElementId) : null
    const ext = host?.category === 'wall' && host.params.function === 'exterior'
    if ((side === 'exterior') !== ext) continue
    ids.push(el.id as ElementId)
  }
  if (ids.length && !dryRun) {
    transact(model, () => {
      for (const id of ids) (maps(model).elements.get(id)?.get('params') as Y.Map<unknown> | undefined)?.delete('heightFt')
    })
  }
  return ids.length
}

/**
 * ELECTRICAL FIXTURES round: mountHeightFt is a PLAIN instance param (never stored
 * on the type — Matt, 2026-08-12: mounting height is where a fixture sits, not
 * what it is). A brand-new instance with no height already supplied (no
 * eyedropper/create-similar pickup riding along) is seeded from the kind's
 * code-height standard (FIXTURE_MOUNT_STANDARD_FT) — the sizeSeries precedent
 * above, applied to a single value instead of a curated catalog.
 */
function seededFixtureMountHeight(model: ModelDoc, category: CategoryId, typeId: TypeId): Record<string, ParamValue> {
  if (category !== 'fixture') return {}
  const kind = String(getType(model, typeId)?.params.fixtureKind ?? 'outlet')
  // CEILING HOSTING round (2026-08-23): mountHeightFt is a wall-kind-only concept
  // now that recessedCan/pendant/flushMount live in this category too — their
  // height derives from the host ceiling (fixtureSolid), never a seeded guess.
  if (!WALL_FIXTURE_KINDS.includes(kind)) return {}
  const std = FIXTURE_MOUNT_STANDARD_FT[kind]
  return typeof std === 'number' ? { mountHeightFt: std } : {}
}

/**
 * WALL INSULATION DEFAULT round (2026-08-18, Matt: "the assumption is we always
 * insulate exterior walls unless we decide to toggle it off. Interior walls are
 * the opposite."). A brand-new wall instance with no `insulation` already
 * supplied (no eyedropper/create-similar pickup riding along) seeds it from the
 * TYPE's own `function` (exterior/interior) — exterior defaults ON, interior
 * defaults OFF. Purely a creation-time default, same precedent as
 * seededOpeningSize/seededFixtureMountHeight above — it's still a free
 * per-instance toggle afterward (and conditionalOn already resets it to false
 * for non-stud cores at resolve time regardless of what's seeded here).
 */
function seededWallInsulation(model: ModelDoc, category: CategoryId, typeId: TypeId): Record<string, ParamValue> {
  if (category !== 'wall') return {}
  const fn = getType(model, typeId)?.params.function
  return { insulation: fn !== 'interior' }
}

export function createElement(
  model: ModelDoc,
  input: {
    category: CategoryId
    typeId: TypeId
    levelId: LevelId
    hostId?: ElementId
    /** the drawing-time phase toggle stamps this so tracing never needs re-tagging
     *  after the fact (docs/bim-plan/09 §7) — absent = untagged ("always existed") */
    phaseCreated?: PhaseId
    params?: Record<string, ParamValue>
  },
): ElementId {
  const id = newElementId()
  const seededSize = seededOpeningSize(model, input.category, input.typeId)
  const seededMount = seededFixtureMountHeight(model, input.category, input.typeId)
  const seededInsulation = seededWallInsulation(model, input.category, input.typeId)
  transact(model, () => {
    maps(model).elements.set(id, recordToYMap({
      id,
      category: input.category,
      typeId: input.typeId,
      levelId: input.levelId,
      ...(input.hostId ? { hostId: input.hostId } : {}),
      ...(input.phaseCreated ? { phaseCreated: input.phaseCreated } : {}),
      params: { ...seededSize, ...seededMount, ...seededInsulation, ...(input.params ?? {}) },
    }))
  })
  return id
}

/**
 * Duplicate an element in place (r11 — "move a copy"): same category/type/level/host,
 * params deep-cloned. Path-shaped params get FRESH node ids so handle refs, snap-node
 * ids, and topology ops on the copy never collide with the original's. Hosted children
 * of the original are NOT copied (matches offsetCopyWall). Returns the new id.
 */
/**
 * COPIES START UNJOINED: walls copied in ONE operation keep the joins they had with EACH
 * OTHER — original A joined to original B becomes copy A joined to copy B (same ends) —
 * and nothing else. Call after the copies have been placed.
 */
export function relinkCopiedWallJoins(model: ModelDoc, pairs: [ElementId, ElementId][]): void {
  const copyOf = new Map(pairs)
  if (!copyOf.size) return
  const { elements } = maps(model)
  transact(model, () => {
    for (const [orig, copy] of pairs) {
      const oem = elements.get(orig), cem = elements.get(copy)
      if (!oem || !cem || oem.get('category') !== 'wall') continue
      const opm = oem.get('params') as Y.Map<unknown>, cpm = cem.get('params') as Y.Map<unknown>
      for (const key of ['startJoin', 'endJoin'] as const) {
        const j = opm.get(key) as { targetId?: ElementId; targetEnd?: 'start' | 'end' } | null | undefined
        const to = j?.targetId ? copyOf.get(j.targetId) : undefined
        if (to && j?.targetEnd) cpm.set(key, { targetId: to, targetEnd: j.targetEnd })
      }
    }
  })
}

export function duplicateElement(model: ModelDoc, id: ElementId): ElementId | null {
  const rec = getElement(model, id)
  if (!rec) return null
  const params = JSON.parse(JSON.stringify(rec.params ?? {})) as Record<string, ParamValue>
  // MODIFY PHASE-2: a duplicate is born UNPINNED — pin protects the original instance,
  // and every copy flow (Move-a-copy, Array) must be free to place the fresh copy
  delete params.pinned
  // CASEWORK RUNS: a copied run member never shares the source's run
  detachCopyIntoOwnRun(params)
  // COPIES START UNJOINED (2026-10-01, Matt: "the copy(ies) should come with no
  // associations with any other walls other than if they were copied with any that had
  // associations in the first place"): a copied wall used to carry the ORIGINAL's corner
  // joins, so the first move of the copy dragged the original's neighbors with it.
  // Joins between walls copied TOGETHER are re-made afterwards (relinkCopiedWallJoins).
  delete params.startJoin
  delete params.endJoin
  // MULLED WINDOWS (2026-10-08): a copied window is its own window — never a member of
  // the source's mulled unit (mullGroups.ts)
  delete params.mullGroupId
  delete params.mullIndex
  // DINING CHAIRS BY COUNT: a copied chair is a chair of its own — no longer one of
  // the table's set (a copied TABLE re-links its own chair copies below)
  for (const k of CHAIR_LINK_KEYS) delete params[k]
  for (const v of Object.values(params)) {
    const path = v as { nodes?: { id?: string }[] } | null
    if (path && typeof path === 'object' && Array.isArray(path.nodes)) {
      for (const n of path.nodes) if (n && typeof n === 'object') n.id = newNodeId()
    }
  }
  const newId = newElementId()
  transact(model, () => {
    maps(model).elements.set(newId, recordToYMap({
      id: newId,
      category: rec.category,
      typeId: rec.typeId,
      levelId: rec.levelId,
      ...(rec.hostId ? { hostId: rec.hostId } : {}),
      // a copy / split / offset keeps the ORIGINAL's phasing (Matt 2026-09-29: "the new
      // element created by the command should have the same phasing properties")
      ...(rec.phaseCreated ? { phaseCreated: rec.phaseCreated } : {}),
      ...(rec.phaseDemolished ? { phaseDemolished: rec.phaseDemolished } : {}),
      params,
    }))
    // DINING CHAIRS BY COUNT: a copied table brings its chairs, linked to the copy
    for (const c of isDiningTable(model, id) ? chairsOfTable(model, id) : []) {
      const cid = duplicateElement(model, c)
      const cpm = cid ? maps(model).elements.get(cid)?.get('params') as Y.Map<unknown> | undefined : undefined
      const src = getElement(model, c)?.params ?? {}
      if (cpm) for (const k of CHAIR_LINK_KEYS) if (src[k] !== undefined) cpm.set(k, k === 'chairOfTableId' ? newId : JSON.parse(JSON.stringify(src[k])))
    }
  })
  return newId
}

// ---------- CHANGES IN A PHASE (phaseChanges.ts, docs/bim-plan/32 §3 B) ----------
// keys that are bookkeeping, never a change
const NEVER_A_CHANGE = new Set([PHASE_CHANGES_KEY, 'styleSetPins', 'phaseSplits', 'phaseAreas', 'pinned', 'phaseCreated', 'phaseDemolished', 'specItemId'])
const NOT_A_CHANGE_KINDS = new Set(['path2d', 'level', 'data'])
/** the edit phase a write to `key` on this element is recorded in, or null = write the element
 *  itself. Only when the app has set an edit phase, the element stands EXISTING in it (built
 *  earlier, not demolished yet), and the key is a look / size setting, not placement. */
export function changePhaseFor(model: ModelDoc, id: ElementId, key: string): string | null {
  // EAVES IN A PHASE (eavePhase.ts): the roof's "Eaves as" switch routes only its eave settings
  const ph = getEditPhase(model.ydoc) ?? (isRoofEaveParam(key) ? eaveEditPhaseFor(model.ydoc, id as string) : null)
  if (!ph || NEVER_A_CHANGE.has(key)) return null
  const rec = getElement(model, id)
  if (!rec) return null
  const cat = getCategory(rec.category)
  const def = cat ? schemaParams(cat.instanceParams).find((d) => d.key === key) : null
  if (!def || def.positional || NOT_A_CHANGE_KINDS.has(def.kind)) return null
  const phases = listPhases(model)
  const fields = hostedPhaseFields(rec as never, (h) => getElement(model, h as ElementId) as never, phases)
  return phaseStatus(phases, fields, ph as never) === 'existing' ? ph : null
}
function writeParam(model: ModelDoc, id: ElementId, pm: Y.Map<unknown>, key: string, value: ParamValue | undefined): void {
  const ph = changePhaseFor(model, id, key)
  if (!ph) {
    if (value === undefined) pm.delete(key)
    else pm.set(key, value)
    return
  }
  const next = withChange(normalizePhaseChanges(pm.get(PHASE_CHANGES_KEY)), ph, key, value === undefined ? UNSET : value)
  if (next.length) pm.set(PHASE_CHANGES_KEY, next)
  else pm.delete(PHASE_CHANGES_KEY)
}
/** "Back to existing": drop one key from the change recorded in `phase` (all keys when absent) */
export function removePhaseChange(model: ModelDoc, id: ElementId, phase: string, key?: string): boolean {
  const em = maps(model).elements.get(id)
  if (!em) return false
  transact(model, () => {
    const pm = em.get('params') as Y.Map<unknown>
    const cur = normalizePhaseChanges(pm.get(PHASE_CHANGES_KEY))
    const next = key ? withChange(cur, phase, key, undefined) : cur.filter((c) => c.phase !== phase)
    if (next.length) pm.set(PHASE_CHANGES_KEY, next)
    else pm.delete(PHASE_CHANGES_KEY)
  })
  return true
}

/** "Back to existing" for one edge attr recorded in `phase` (EAVES IN A PHASE) */
export function removeEdgePhaseChange(model: ModelDoc, id: ElementId, phase: string, pathKey: string, nodeId: string, key: string): boolean {
  const em = maps(model).elements.get(id)
  if (!em) return false
  transact(model, () => {
    const pm = em.get('params') as Y.Map<unknown>
    const next = withEdgeChange(normalizePhaseChanges(pm.get(PHASE_CHANGES_KEY)), phase, pathKey, nodeId, key, undefined)
    if (next.length) pm.set(PHASE_CHANGES_KEY, next)
    else pm.delete(PHASE_CHANGES_KEY)
  })
  return true
}

/** Set one instance param (Path2D values replace atomically by construction). In a later
 *  drawing phase, a look setting on an existing element is recorded as a change in that phase. */
export function setElementParam(model: ModelDoc, id: ElementId, key: string, value: ParamValue): boolean {
  const em = maps(model).elements.get(id)
  if (!em) return false
  transact(model, () => {
    const pm = em.get('params') as Y.Map<unknown>
    writeParam(model, id, pm, key, value)
  })
  return true
}

/**
 * ROOF SETTINGS CLEANUP (2026-10-08): DELETE stored instance params — the element goes back to
 * following its project / type / default for each. A stored null is NOT the same (it resolves
 * as null, not the default), so "follow again" must delete. One transaction = one undo step.
 */
export function clearElementParams(model: ModelDoc, id: ElementId, keys: string[]): number {
  const em = maps(model).elements.get(id)
  if (!em) return 0
  let n = 0
  transact(model, () => {
    const pm = em.get('params') as Y.Map<unknown>
    for (const k of keys) {
      if (changePhaseFor(model, id, k)) { writeParam(model, id, pm, k, undefined); n++ }
      else if (pm.has(k)) { pm.delete(k); n++ }
    }
  })
  return n
}

/**
 * Set MULTIPLE instance params atomically — one transact = one undo step (e.g. a
 * door/window's size-series dropdown sets widthFt + heightFt together from one user
 * action; two separate setElementParam calls would cost two undo steps for one pick).
 */
export function setElementParams(model: ModelDoc, id: ElementId, patch: Record<string, ParamValue>): boolean {
  const em = maps(model).elements.get(id)
  if (!em) return false
  transact(model, () => {
    const pm = em.get('params') as Y.Map<unknown>
    for (const [k, v] of Object.entries(patch)) writeParam(model, id, pm, k, v)
  })
  return true
}

/**
 * Param write with SEMANTIC routing — the palette's single write path (2.6a). Params
 * whose meaning is "re-designate, don't move" need a compound command, not a raw set:
 * a wall's `locationLine` must transform the stored path in the same transaction so the
 * body stays put (docs/bim-plan/06 §4.3). Everything else falls through to
 * setElementParam. The routing lives HERE (beside the schemas) so the palette stays free
 * of category branches.
 */
export function applyParamEdit(model: ModelDoc, id: ElementId, key: string, value: ParamValue): boolean {
  const em = maps(model).elements.get(id)
  if (!em) return false
  if (key === 'locationLine' && em.get('category') === 'wall') {
    return setLocationLine(model, id, value as LocationLine)
  }
  // UPPER HEIGHT (2026-10-15): an upper's typed bottom is the box's own mount-height override
  // (the Casework Studio's `boxStyle.mountHeightFt`) — one source, never a second param
  // BOX DEPTH (2026-10-21): a box's typed depth is its `boxStyle.depthFt` the same way
  const boxKey = key === 'upperBottomFt' ? 'mountHeightFt' : key === 'boxDepthFt' ? 'depthFt' : null
  if (boxKey && em.get('category') === 'casework') {
    transact(model, () => {
      const pm = em.get('params') as Y.Map<unknown>
      const cur = pm.get('boxStyle')
      const box = { ...(cur && typeof cur === 'object' && !Array.isArray(cur) ? cur as Record<string, unknown> : {}) }
      if (typeof value === 'number' && value > 0) box[boxKey] = value
      else delete box[boxKey]
      if (Object.keys(box).length) pm.set('boxStyle', box)
      else pm.delete('boxStyle')
    })
    return true
  }
  // STANDARD APPLIANCE SIZES (2026-10-15): picking a standard size writes its width, depth

  // and height onto the appliance; typing a dimension of a standard-sized one makes it Custom
  if (key === 'applianceSize' && em.get('category') === 'object') {
    const kind = getType(model, em.get('typeId') as TypeId)?.params.objectKind
    const size = applianceSizeOf(kind, value)
    transact(model, () => {
      const pm = em.get('params') as Y.Map<unknown>
      pm.set('applianceSize', size ? size.value : '')
      if (size) { pm.set('sizeWidthFt', size.w / 12); pm.set('sizeDepthFt', size.d / 12); pm.set('sizeHeightFt', size.h / 12) }
    })
    return true
  }
  if ((key === 'sizeWidthFt' || key === 'sizeDepthFt' || key === 'sizeHeightFt') && em.get('category') === 'object') {
    const pm = em.get('params') as Y.Map<unknown>
    if (pm.get('applianceSize')) {
      transact(model, () => { pm.set(key, value); pm.set('applianceSize', '') })
      return true
    }
  }
  // DINING CHAIRS BY COUNT: a table's chair count (re)generates its chairs

  if (key === 'chairCount' && isDiningTable(model, id)) {
    return setDiningChairCount(model, id, typeof value === 'number' ? value : 0)
  }
  // CASEWORK RUNS: a typed width on a run box SIZES it and the run re-lays (its
  // length held; unsized boxes absorb the difference)
  if (key === 'widthFt' && typeof value === 'number' && runIdOf(model, id)) {
    return setRunBoxWidth(model, id, value)
  }
  // TERRAIN DECOUPLE round: turning the foundation off retires the captured
  // terrain profile in the same transaction — dead data otherwise, and
  // re-enabling should re-capture fresh, never resurrect a stale fit.
  if (key === 'footingEnabled' && value === false && em.get('category') === 'wall') {
    transact(model, () => {
      const pm = em.get('params') as Y.Map<unknown>
      pm.set('footingEnabled', false)
      if (pm.has('terrainFit')) pm.delete('terrainFit')
    })
    return true
  }
  return setElementParam(model, id, key, value)
}

/**
 * TERRAIN DECOUPLE round (docs/bim-plan/19): write captured terrain profiles onto
 * footed walls — ONE transaction (one undo step) for the whole batch, since a
 * capture is one user action whether it re-fits one wall or a surveyed set. The
 * profiles come from the worker's sampler (`sampleTerrainFits`); the CLIENT stamps
 * `capturedAt` before calling (clock reads stay out of the kernel/worker).
 * Passing `null` for an id clears its fit (back to live derivation).
 */
export function setTerrainFits(
  model: ModelDoc,
  entries: { id: ElementId; fit: ParamValue }[],
): number {
  const { elements } = maps(model)
  let wrote = 0
  transact(model, () => {
    for (const e of entries) {
      const em = elements.get(e.id)
      if (!em || em.get('category') !== 'wall') continue
      const pm = em.get('params') as Y.Map<unknown>
      if (e.fit === null) {
        if (pm.has('terrainFit')) { pm.delete('terrainFit'); wrote++ }
      } else {
        pm.set('terrainFit', e.fit)
        wrote++
      }
    }
  })
  return wrote
}

/**
 * CASEWORK DIMENSION MIGRATION (casework/naming round, 2026-08-09 — docs/MODELER.md):
 * widthFt/heightFt moved from casework TYPE params to per-element INSTANCE params
 * (many widths per type; height still defaults from the type but a nullable instance
 * override). An element created before this round has neither key in its OWN params —
 * resolving it would silently fall back to the instance schema's bare defaults (2' /
 * the type's current height), NOT necessarily the dimensions it was actually built
 * with if the type's width has since drifted. Idempotent, pre-undo, run once per doc
 * open (ModelerPage.jsx, alongside ensureDefaultLibrary/ensurePhases), same one-
 * transaction pattern as `commands/views.ts#normalizeViewOrientations`: for every
 * casework element missing its own widthFt, stamp BOTH widthFt and heightFt from the
 * TYPE's raw params blob — the schema no longer reads those keys off the type, but
 * nothing has deleted them from storage, so they're still there to seed from. Zero
 * geometry movement. heightFt is stamped explicitly (even though the nullable
 * shadow-to-type fallback already reproduces the same value today) so a LATER edit to
 * the type's height never retroactively reflows cabinetry that was already built.
 */
export function migrateCaseworkInstanceDims(model: ModelDoc): number {
  const fixes: { id: ElementId; widthFt?: ParamValue; heightFt?: ParamValue }[] = []
  for (const el of listElements(model)) {
    if (el.category !== 'casework' || el.params.widthFt !== undefined) continue
    const type = getType(model, el.typeId)
    const legacyWidth = type?.params.widthFt
    const legacyHeight = type?.params.heightFt
    if (legacyWidth === undefined && legacyHeight === undefined) continue
    fixes.push({ id: el.id, widthFt: legacyWidth, heightFt: legacyHeight })
  }
  if (fixes.length) {
    transact(model, () => {
      for (const f of fixes) {
        const em = maps(model).elements.get(f.id)
        if (!em) continue
        const pm = em.get('params') as Y.Map<unknown>
        if (f.widthFt !== undefined) pm.set('widthFt', f.widthFt)
        if (f.heightFt !== undefined) pm.set('heightFt', f.heightFt)
      }
    })
  }
  return fixes.length
}

/**
 * FREESTANDING round (2026-08-19, docs/MODELER.md): casework moved from wall-hosted
 * (hostId + hostOffsetFt + sideFlip) to freestanding (path + rotationDeg — the OBJECTS
 * round's own model; see categories/casework/schema.ts). Idempotent, pre-undo, run
 * once per doc open (ModelerPage.jsx, alongside ensureDefaultLibrary/ensurePhases/
 * migrateCaseworkInstanceDims), same one-transaction pattern: for every casework
 * element that still carries a hostId, compute its CURRENT world anchor + facing from
 * the exact math the old caseworkSolid used (host path station + wall-face offset +
 * sideFlip), stamp `path`/`rotationDeg` from that, then drop `hostId`/`hostOffsetFt`/
 * `sideFlip` — reproducing the identical world position/facing the element already
 * had, so nothing visually moves. An element whose host has vanished or lost its path
 * is left untouched (nothing to migrate FROM) rather than guessed at.
 */
export function migrateCaseworkToFreestanding(model: ModelDoc): number {
  const fixes: { id: ElementId; path: Path2D; rotationDeg: number }[] = []
  for (const el of listElements(model)) {
    if (el.category !== 'casework' || !el.hostId || el.params.path !== undefined) continue
    const host = resolveElement(model, el.hostId)
    const hPath = host?.params.path as Path2D | null | undefined
    if (!host || host.category !== 'wall' || !host.level || !hPath?.nodes || hPath.nodes.length < 2) continue
    const t = host.assemblyThicknessFt
    const pts = hPath.nodes.map((n) => n.pt)
    const s = (el.params.hostOffsetFt as number) || 0
    const { pt, dir } = polylinePointAt(pts, s)
    const delta = centerlineOffsetFt(host.params.locationLine, host.params.flipped, host.assembly)
    const leftNormal: Pt2 = [-dir[1], dir[0]]
    const sideFlip = !!el.params.sideFlip
    const flip = sideFlip ? -1 : 1
    const inward = (host.params.flipped ? 1 : -1) * flip
    const faceOff = delta + inward * (t / 2)
    const px = pt[0] + leftNormal[0] * faceOff, py = pt[1] + leftNormal[1] * faceOff
    const rotZ = Math.atan2(dir[1], dir[0]) + (host.params.flipped ? 0 : Math.PI) + (sideFlip ? Math.PI : 0)
    let rotationDeg = ((rotZ * 180) / Math.PI) % 360
    if (rotationDeg > 180) rotationDeg -= 360
    if (rotationDeg <= -180) rotationDeg += 360
    fixes.push({
      id: el.id,
      path: { closed: false, nodes: [{ id: newNodeId(), pt: [px, py] }] },
      rotationDeg,
    })
  }
  if (fixes.length) {
    transact(model, () => {
      for (const f of fixes) {
        const em = maps(model).elements.get(f.id)
        if (!em) continue
        em.delete('hostId')
        const pm = em.get('params') as Y.Map<unknown>
        pm.set('path', f.path)
        pm.set('rotationDeg', f.rotationDeg)
        pm.delete('hostOffsetFt')
        pm.delete('sideFlip')
      }
    })
  }
  return fixes.length
}

/**
 * CEILING HOSTING round (2026-08-23, Matt: "Lights should be under electrical
 * fixtures, not objects... We already have sconces there."): recessedCan/
 * pendant/flushMount move from the `object` category (Round C, freestanding —
 * §6 Q3's "no hosting work" call) to `fixture`, hosted into a real ceiling
 * element — reversing that call now that Matt's seen it live. Idempotent,
 * pre-undo, run once per doc open (ModelerPage.jsx, the
 * migrateCaseworkToFreestanding precedent, mirror direction). SAME element id
 * (so existing switch `switchTargetIds` pairings never go dangling) and SAME
 * type id (the type is migrated too — `category` → 'fixture', `objectKind` →
 * `fixtureKind`, both stored fields — so every other element sharing that type
 * migrates for free in the same pass; a type is never shared across kinds, so
 * this is always safe). `heightOffsetFt` is dropped (height now derives from
 * the host ceiling); `path`/`rotationDeg`/`widthFt`/`depthFt`/`heightFt`/
 * `trimShape`/`lightIntensity`/`surfaceMaterial` carry over untouched —
 * identical param names/shapes in both categories for these three kinds.
 *
 * HARDENED 2026-08-24 (Matt, caps: "ALL LIGHTING FIXTURES SHOULD BE IN THE
 * ELECTRICAL FIXTURES CATEGORY, NOT OBJECTS"): the first cut only migrated
 * anything when at least one placed light sat under a drawn ceiling — a model
 * with lights but NO ceilings drawn yet kept every light (and its types, so
 * the Object tool kept listing them) in the object category indefinitely. Now:
 * - TYPES migrate UNCONDITIONALLY — every object-category type whose
 *   objectKind is a ceiling-light kind flips to fixture, elements or not.
 * - ELEMENTS migrate unconditionally too; `hostId` is set when a ceiling
 *   covers the anchor, and simply absent when none does — an UNHOSTED ceiling
 *   light renders at a standard fallback height (UNHOSTED_CEILING_LIGHT_FT,
 *   fixture/generate3d.ts) instead of being stranded as an object.
 * - Each open also RE-ADOPTS hosts: any fixture-category ceiling light still
 *   unhosted gets attached the moment a ceiling exists under it — so drawing
 *   the ceiling after the lights heals them automatically.
 */
/**
 * TRIM REMOVED (2026-09-30, Matt: "Delete trim category and families … Remove
 * everything"): the trim category is gone from the app. Idempotent, pre-undo, run once
 * per doc open (ModelerPage.jsx, beside the other migrateX calls): deletes every
 * legacy trim ELEMENT and trim TYPE, drops a sheet's trim-schedule toggle and a view's
 * trim-linework toggle. Reads the raw stored `category` string — 'trim' is no longer a
 * CategoryId, so nothing typed can name it any more. Returns what it removed.
 */
/**
 * FLOATING FURNITURE FIX (2026-10-01): an object that stands on the floor but was
 * auto-hosted onto casework or another object (its anchor fell inside that
 * footprint at placement — a coffee table riding up onto the sofa beside it) moves
 * back down: re-hosted onto the floor slab under it, or unhosted onto its level.
 * Counter-sitting kinds (sinks) keep their counter. Idempotent; returns the count.
 */
/**
 * SINKS ON COUNTERS (2026-10-01, Matt: "make sure we can see them when they are placed
 * over the top of a casework base"): a sink whose spot is over a base cabinet but which
 * is hosted to the floor (or nothing) moves onto that cabinet, so its rim sits ON the
 * counter instead of inside it (a 36" base's counter top is ~37½" — a 3' rim measured
 * from the floor is hidden in the counter). Idempotent; returns the count.
 */
export function migrateSinksOntoCounters(model: ModelDoc): number {
  const fixes: { id: string; hostId: string }[] = []
  for (const el of resolveAllElements(model)) {
    if (el.category !== 'object') continue
    const kind = String(el.typeParams?.objectKind || '')
    if (kind !== 'kitchenSink' && kind !== 'bathSink') continue
    const host = el.hostId ? resolveElement(model, el.hostId) : null
    if (host && (host.category === 'casework' || host.category === 'object')) continue
    const anchor = objectAnchorPoint(el)
    const counter = anchor ? surfaceHostAtPoint(model, el.levelId, anchor, el.id, kind) : null
    if (counter && counter.category === 'casework') fixes.push({ id: el.id, hostId: counter.id })
  }
  if (!fixes.length) return 0
  const m = maps(model)
  transact(model, () => {
    for (const f of fixes) m.elements.get(f.id)?.set('hostId', f.hostId)
  })
  return fixes.length
}

export function migrateFloorStandingObjectHosts(model: ModelDoc): number {
  const fixes: { id: string; hostId: string | null }[] = []
  for (const el of resolveAllElements(model)) {
    if (el.category !== 'object' || !el.hostId) continue
    const kind = String(el.typeParams?.objectKind || '')
    if (SURFACE_SITTING_OBJECT_KINDS.includes(kind)) continue
    const host = resolveElement(model, el.hostId)
    if (host && host.category === 'floor') continue
    const anchor = objectAnchorPoint(el)
    const floor = anchor ? surfaceHostAtPoint(model, el.levelId, anchor, el.id, kind) : null
    fixes.push({ id: el.id, hostId: floor?.id ?? null })
  }
  if (!fixes.length) return 0
  const m = maps(model)
  transact(model, () => {
    for (const f of fixes) {
      const em = m.elements.get(f.id)
      if (!em) continue
      if (f.hostId) em.set('hostId', f.hostId)
      else em.delete('hostId')
    }
  })
  return fixes.length
}

export function migrateRemoveTrim(model: ModelDoc): number {
  const m = maps(model)
  const elIds: string[] = []
  m.elements.forEach((em, id) => { if (em.get('category') === 'trim') elIds.push(id) })
  const typeIds: string[] = []
  m.types.forEach((tm, id) => { if (tm.get('category') === 'trim') typeIds.push(id) })
  const sheetIds: string[] = []
  m.sheets.forEach((sm, id) => {
    const sc = sm.get('schedules') as Record<string, unknown> | undefined
    if (sc && 'trim' in sc) sheetIds.push(id)
  })
  const viewIds: string[] = []
  m.views.forEach((vm, id) => {
    const an = vm.get('annotations') as Record<string, unknown> | undefined
    if (an && 'trim' in an) viewIds.push(id)
  })
  const n = elIds.length + typeIds.length + sheetIds.length + viewIds.length
  if (!n) return 0
  transact(model, () => {
    for (const id of elIds) m.elements.delete(id)
    for (const id of typeIds) m.types.delete(id)
    for (const id of sheetIds) {
      const sm = m.sheets.get(id)!
      const next = { ...(sm.get('schedules') as Record<string, unknown>) }
      delete next.trim
      if (Object.keys(next).length) sm.set('schedules', next)
      else sm.delete('schedules')
    }
    for (const id of viewIds) {
      const vm = m.views.get(id)!
      const next = { ...(vm.get('annotations') as Record<string, unknown>) }
      delete next.trim
      if (Object.keys(next).length) vm.set('annotations', next)
      else vm.delete('annotations')
    }
  })
  return n
}

export function migrateCeilingLightsToFixture(model: ModelDoc): number {
  const typeFixes = new Set<TypeId>()
  const elFixes: { id: ElementId; hostId: ElementId | null }[] = []
  const hostAdopts: { id: ElementId; hostId: ElementId }[] = []
  for (const t of listTypes(model)) {
    if (t.category !== 'object') continue
    if (CEILING_FIXTURE_KINDS.includes(String(t.params.objectKind ?? ''))) typeFixes.add(t.id)
  }
  for (const el of listElements(model)) {
    const type = getType(model, el.typeId)
    if (el.category === 'object') {
      if (!typeFixes.has(el.typeId)) continue
      const anchor = objectAnchorPoint({ params: el.params })
      const ceiling = anchor ? ceilingAtPoint(model, el.levelId, anchor) : null
      elFixes.push({ id: el.id, hostId: ceiling?.id ?? null })
    } else if (el.category === 'fixture' && !el.hostId
      && CEILING_FIXTURE_KINDS.includes(String(type?.params.fixtureKind ?? ''))) {
      // re-adopt pass: a ceiling drawn AFTER the light was migrated/placed
      const anchor = objectAnchorPoint({ params: el.params })
      const ceiling = anchor ? ceilingAtPoint(model, el.levelId, anchor) : null
      if (ceiling) hostAdopts.push({ id: el.id, hostId: ceiling.id })
    }
  }
  if (typeFixes.size || elFixes.length || hostAdopts.length) {
    transact(model, () => {
      for (const typeId of typeFixes) {
        const tm = maps(model).types.get(typeId)
        if (!tm) continue
        tm.set('category', 'fixture')
        const tpm = tm.get('params') as Y.Map<unknown>
        tpm.set('fixtureKind', tpm.get('objectKind'))
        tpm.delete('objectKind')
      }
      for (const f of elFixes) {
        const em = maps(model).elements.get(f.id)
        if (!em) continue
        em.set('category', 'fixture')
        if (f.hostId) em.set('hostId', f.hostId)
        const pm = em.get('params') as Y.Map<unknown>
        pm.delete('heightOffsetFt')
      }
      for (const a of hostAdopts) {
        maps(model).elements.get(a.id)?.set('hostId', a.hostId)
      }
    })
  }
  return elFixes.length + hostAdopts.length + (elFixes.length ? 0 : typeFixes.size)
}

/**
 * OPENING CONFIGURATION GOVERNANCE round (2026-08-10, docs/MODELER.md): widthFt/
 * heightFt/muntinPattern/muntinRows/muntinCols moved from door/window TYPE params to
 * per-element INSTANCE params — the exact same migration shape as
 * migrateCaseworkInstanceDims above (see its docstring for the full reasoning: an
 * element built before this round has none of these keys in its OWN params, so
 * resolving it would silently fall back to the instance schema's bare defaults instead
 * of the size/muntins it was actually built with). Idempotent, pre-undo, run once per
 * doc open (ModelerPage.jsx). Zero geometry movement — every value stamped here is
 * exactly what the type's raw (now schema-orphaned, but still stored) params already
 * held. Muntins are only stamped when the type actually had a muntin pattern set
 * (never forces `muntinPattern: 'none'` onto a type that never had the key at all).
 */
export function migrateOpeningInstanceDims(model: ModelDoc): number {
  const fixes: {
    id: ElementId; widthFt?: ParamValue; heightFt?: ParamValue
    muntinPattern?: ParamValue; muntinRows?: ParamValue; muntinCols?: ParamValue
  }[] = []
  for (const el of listElements(model)) {
    if ((el.category !== 'door' && el.category !== 'window') || el.params.widthFt !== undefined) continue
    const type = getType(model, el.typeId)
    const legacyWidth = type?.params.widthFt
    const legacyHeight = type?.params.heightFt
    if (legacyWidth === undefined && legacyHeight === undefined) continue
    const fix: (typeof fixes)[number] = { id: el.id, widthFt: legacyWidth, heightFt: legacyHeight }
    if (type?.params.muntinPattern !== undefined) {
      fix.muntinPattern = type.params.muntinPattern
      fix.muntinRows = type.params.muntinRows
      fix.muntinCols = type.params.muntinCols
    }
    fixes.push(fix)
  }
  if (fixes.length) {
    transact(model, () => {
      for (const f of fixes) {
        const em = maps(model).elements.get(f.id)
        if (!em) continue
        const pm = em.get('params') as Y.Map<unknown>
        if (f.widthFt !== undefined) pm.set('widthFt', f.widthFt)
        if (f.heightFt !== undefined) pm.set('heightFt', f.heightFt)
        if (f.muntinPattern !== undefined) pm.set('muntinPattern', f.muntinPattern)
        if (f.muntinRows !== undefined) pm.set('muntinRows', f.muntinRows)
        if (f.muntinCols !== undefined) pm.set('muntinCols', f.muntinCols)
      }
    })
  }
  return fixes.length
}

/**
 * SIDELIGHTS PER-SIDE per-instance-size/muntins follow-up (2026-08-19, Matt: "the
 * size of sidelites editable like the size of doors... not stuck in type
 * parameters"): sidelightLeftWidthFt/sidelightRightWidthFt/transomHeightFt moved
 * from TYPE to INSTANCE params (the migrateOpeningInstanceDims precedent right
 * above) — seed each door ELEMENT's instance width/height from its TYPE's own
 * (now-legacy, still-stored-but-no-longer-schema-declared) width value the first
 * time this runs, so no existing door's built geometry moves. Independent of
 * migrateOpeningInstanceDims's own widthFt guard — a door may have already
 * migrated its leaf widthFt/heightFt in an earlier round without ever having run
 * this one, so each of the three fields gets its own per-field guard instead of
 * one shared skip check. No migration needed for the new muntin pattern/rows/cols
 * fields alongside these — they never existed as type params before this round,
 * so every pre-round door correctly starts at the schema default ('none').
 */
export function migrateSidelightInstanceDims(model: ModelDoc): number {
  const fixes: {
    id: ElementId
    sidelightLeftWidthFt?: ParamValue; sidelightRightWidthFt?: ParamValue; transomHeightFt?: ParamValue
  }[] = []
  for (const el of listElements(model)) {
    if (el.category !== 'door') continue
    const type = getType(model, el.typeId)
    if (!type) continue
    const fix: (typeof fixes)[number] = { id: el.id }
    let any = false
    if (type.params.sidelightLeft === true && el.params.sidelightLeftWidthFt === undefined
      && type.params.sidelightLeftWidthFt !== undefined) {
      fix.sidelightLeftWidthFt = type.params.sidelightLeftWidthFt
      any = true
    }
    if (type.params.sidelightRight === true && el.params.sidelightRightWidthFt === undefined
      && type.params.sidelightRightWidthFt !== undefined) {
      fix.sidelightRightWidthFt = type.params.sidelightRightWidthFt
      any = true
    }
    if (type.params.transom === true && el.params.transomHeightFt === undefined
      && type.params.transomHeightFt !== undefined) {
      fix.transomHeightFt = type.params.transomHeightFt
      any = true
    }
    if (any) fixes.push(fix)
  }
  if (fixes.length) {
    transact(model, () => {
      for (const f of fixes) {
        const em = maps(model).elements.get(f.id)
        if (!em) continue
        const pm = em.get('params') as Y.Map<unknown>
        if (f.sidelightLeftWidthFt !== undefined) pm.set('sidelightLeftWidthFt', f.sidelightLeftWidthFt)
        if (f.sidelightRightWidthFt !== undefined) pm.set('sidelightRightWidthFt', f.sidelightRightWidthFt)
        if (f.transomHeightFt !== undefined) pm.set('transomHeightFt', f.transomHeightFt)
      }
    })
  }
  return fixes.length
}

/**
 * HEAD SHAPE PER INSTANCE (2026-10-30, Matt): a door/window's head shape (headType) and arch
 * rise (headRiseFt) moved from TYPE to INSTANCE params — the migrateOpeningInstanceDims
 * precedent above. Seed every door/window that has no head of its own from its type's
 * (now schema-orphaned, still stored) values, so no built opening changes shape. Runs ONCE
 * per model (settings flag): after it, a type's leftover head is ignored, and a door placed
 * later starts flat like any other new door. Only a shaped head is stamped — a flat type
 * needs nothing (flat is the default). Pre-undo, at doc open (ModelerPage.jsx).
 */
const HEADS_MIGRATED_KEY = 'migratedOpeningHeadsToInstance'
export function migrateOpeningHeadsToInstance(model: ModelDoc): number {
  if (maps(model).settings.get(HEADS_MIGRATED_KEY)) return 0
  const fixes: { id: ElementId; headType: ParamValue; headRiseFt?: ParamValue }[] = []
  for (const el of listElements(model)) {
    if ((el.category !== 'door' && el.category !== 'window') || el.params.headType !== undefined) continue
    const type = getType(model, el.typeId)
    const ht = type?.params.headType
    if (ht === undefined || ht === 'flat') continue
    fixes.push({ id: el.id, headType: ht, headRiseFt: type?.params.headRiseFt })
  }
  transact(model, () => {
    for (const f of fixes) {
      const pm = maps(model).elements.get(f.id)?.get('params') as Y.Map<unknown> | undefined
      if (!pm) continue
      pm.set('headType', f.headType)
      if (f.headRiseFt !== undefined && !pm.has('headRiseFt')) pm.set('headRiseFt', f.headRiseFt)
    }
    maps(model).settings.set(HEADS_MIGRATED_KEY, true)
  })
  return fixes.length
}

/**
 * PLANT HOSTING (2026-10-05, Matt:"plantings to host to the slab, soil, or terrain below
 * them. Allow us to set a host like we can with fences and railings"): set (or clear, null)
 * an element's surface host WITHOUT moving it to the host's level — a plant on the Main
 * Level stays on the Main Level's plan when it hosts onto a terrain modeled on another
 * level. One undo step.
 */
export function setElementHost(model: ModelDoc, id: ElementId, hostId: ElementId | null): boolean {
  const em = maps(model).elements.get(id)
  if (!em) return false
  if (hostId && !getElement(model, hostId)) return false
  transact(model, () => {
    if (hostId) em.set('hostId', hostId)
    else em.delete('hostId')
  })
  return true
}

/**
 * WALK W2 grab-and-carry (docs/MODELER.md "WALK MODE PHASE W2"): land a HOSTED element
 * on a (possibly different) host in ONE transaction = one undo step — hostId and
 * levelId follow the new host, and the positional params that place it there
 * (hostOffsetFt, sideFlip, …) ride the same step, so undo restores the whole move
 * atomically instead of leaving the element re-hosted at a stale station.
 */
export function rehostElement(
  model: ModelDoc,
  id: ElementId,
  hostId: ElementId,
  params: Record<string, ParamValue> = {},
): boolean {
  const em = maps(model).elements.get(id)
  if (!em) return false
  const host = getElement(model, hostId)
  if (!host) return false
  transact(model, () => {
    em.set('hostId', hostId)
    em.set('levelId', host.levelId)
    const pm = em.get('params') as Y.Map<unknown>
    for (const [k, v] of Object.entries(params)) pm.set(k, v)
  })
  return true
}

/**
 * MULTI-HOST round follow-up (2026-08-25): a railing's host moved from a single
 * record-level `hostId` (the EXPLICIT HOST PICK round's first cut, same day) to a
 * `params.hostIds` array — any number of stairs/ramps/floors/topo at once, the wall
 * attach model (topAttachIds). Idempotent, pre-undo, run once per doc open
 * (ModelerPage.jsx, alongside the other migrateXToY calls): any railing still
 * carrying the legacy `hostId` and no `hostIds` array yet gets `hostIds: [hostId]`
 * seeded and the legacy field dropped — same single host, same resolved height, just
 * expressed in the new array shape so every consumer only ever reads one field. (The
 * bim-geometry resolve path also reads the legacy field directly as a fallback, for
 * any snapshot/export built before this migration ever ran.)
 */
export function migrateRailingHostToHostIds(model: ModelDoc): number {
  const fixes: { id: ElementId; hostId: ElementId }[] = []
  for (const el of listElements(model)) {
    if (el.category !== 'railing' || !el.hostId) continue
    const hostIds = el.params.hostIds
    if (Array.isArray(hostIds) && hostIds.length) continue
    fixes.push({ id: el.id, hostId: el.hostId })
  }
  if (fixes.length) {
    transact(model, () => {
      for (const f of fixes) {
        const em = maps(model).elements.get(f.id)
        if (!em) continue
        em.delete('hostId')
        const pm = em.get('params') as Y.Map<unknown>
        pm.set('hostIds', [f.hostId])
      }
    })
  }
  return fixes.length
}

export function setElementType(model: ModelDoc, id: ElementId, typeId: TypeId): boolean {
  const em = maps(model).elements.get(id)
  if (!em) return false
  transact(model, () => { em.set('typeId', typeId) })
  return true
}

export function setElementLevel(model: ModelDoc, id: ElementId, levelId: LevelId): boolean {
  const em = maps(model).elements.get(id)
  if (!em) return false
  // OPENING HOSTING round: a door/window whose wall doesn't stand at the new level
  // re-hosts into the wall at the same spot there (or is refused — never left
  // floating above its wall cutting nothing)
  const cat = em.get('category')
  if ((cat === 'door' || cat === 'window') && em.get('hostId')) return setOpeningLevel(model, id, levelId).ok
  transact(model, () => { em.set('levelId', levelId) })
  return true
}

/** `phaseId: null` clears the field (back to "always existed" — untagged). */
export function setElementPhaseCreated(model: ModelDoc, id: ElementId, phaseId: PhaseId | null): boolean {
  const em = maps(model).elements.get(id)
  if (!em) return false
  transact(model, () => {
    if (phaseId === null) em.delete('phaseCreated')
    else em.set('phaseCreated', phaseId)
  })
  return true
}

/** `phaseId: null` clears the field (never demolished). */
export function setElementPhaseDemolished(model: ModelDoc, id: ElementId, phaseId: PhaseId | null): boolean {
  const em = maps(model).elements.get(id)
  if (!em) return false
  transact(model, () => {
    if (phaseId === null) em.delete('phaseDemolished')
    else em.set('phaseDemolished', phaseId)
  })
  return true
}

/**
 * IMPOSSIBLE DEMOLITION cleanup (2026-10-16, Dolle Residence): an element whose own
 * `phaseDemolished` is EARLIER than its own `phaseCreated` (two New Construction dining
 * chairs carried "demolished in Existing Conditions") can't exist; `phasing.ts#phaseStatus`
 * already ignores such a demolition on read, this drops the stored field once on doc
 * open so the properties palette stops reporting it. Same-phase built+demolished is
 * left alone (temporary work is real). Idempotent; returns the count cleared.
 */
export function migrateImpossibleDemolition(model: ModelDoc): number {
  const phases = listPhases(model)
  if (phases.length < 2) return 0
  const order = new Map(phases.map((p) => [p.id as string, p.order]))
  const ids: ElementId[] = []
  for (const el of listElements(model)) {
    if (!el.phaseCreated || !el.phaseDemolished) continue
    const c = order.get(el.phaseCreated as string), d = order.get(el.phaseDemolished as string)
    if (c != null && d != null && d < c) ids.push(el.id)
  }
  if (!ids.length) return 0
  const els = maps(model).elements
  transact(model, () => { for (const id of ids) els.get(id)?.delete('phaseDemolished') })
  return ids.length
}

// ---------------------------------------------------------------------------
// PHASE SPLIT round (docs/MODELER.md): closed-boundary elements (floor/ceiling in
// v1) carry an ordered `phaseSplits` list — see types.ts#PhaseSplitEntry and
// phaseSplit.ts#resolvePhaseRegions for how they resolve into regions. Append-only
// array param, same atomic-replace convention as Path2D; each command reads the
// CURRENT array off the raw element record (never the resolved/merged one — this
// is the source array itself) and writes the whole thing back in one transaction.
// ---------------------------------------------------------------------------

function rawPhaseSplits(model: ModelDoc, id: ElementId): PhaseSplitEntry[] {
  const el = getElement(model, id)
  return (el?.params.phaseSplits as PhaseSplitEntry[] | null | undefined) ?? []
}

/** Adds one cut. `tag` sets only the fields it includes — leave a field absent to
 *  inherit the region's prior tags (see PhaseSplitEntry). Returns the new split's
 *  id, or null if the element doesn't exist. */
export function addPhaseSplit(
  model: ModelDoc, id: ElementId, a: Pt2, b: Pt2, tagSide: Pt2,
  tag: { phaseCreated?: PhaseId; phaseDemolished?: PhaseId } = {},
): PhaseSplitId | null {
  if (!getElement(model, id)) return null
  const splitId = newPhaseSplitId()
  const entry: PhaseSplitEntry = { id: splitId, a, b, tagSide, ...tag }
  setElementParam(model, id, 'phaseSplits', [...rawPhaseSplits(model, id), entry])
  return splitId
}

export function removePhaseSplit(model: ModelDoc, id: ElementId, splitId: PhaseSplitId): boolean {
  const existing = rawPhaseSplits(model, id)
  if (!existing.some((s) => s.id === splitId)) return false
  setElementParam(model, id, 'phaseSplits', existing.filter((s) => s.id !== splitId))
  return true
}

/** `null` on either field clears it back to "inherit"; `undefined` leaves it as-is. */
export function setPhaseSplitTag(
  model: ModelDoc, id: ElementId, splitId: PhaseSplitId,
  tag: { phaseCreated?: PhaseId | null; phaseDemolished?: PhaseId | null },
): boolean {
  const existing = rawPhaseSplits(model, id)
  const idx = existing.findIndex((s) => s.id === splitId)
  if (idx < 0) return false
  const cur: PhaseSplitEntry = { ...existing[idx] }
  if (tag.phaseCreated === null) delete cur.phaseCreated
  else if (tag.phaseCreated !== undefined) cur.phaseCreated = tag.phaseCreated
  if (tag.phaseDemolished === null) delete cur.phaseDemolished
  else if (tag.phaseDemolished !== undefined) cur.phaseDemolished = tag.phaseDemolished
  const next = [...existing]
  next[idx] = cur
  setElementParam(model, id, 'phaseSplits', next)
  return true
}

// PHASE AREAS (2026-10-30): the outline editor writes the whole list at Finish (one undo
// step); `clearSplits` retires the element's old straight cuts in the same transaction,
// since the editor opened them as areas.
export function setPhaseAreas(
  model: ModelDoc, id: ElementId, areas: PhaseAreaEntry[], opts: { clearSplits?: boolean } = {},
): boolean {
  if (!getElement(model, id)) return false
  transact(model, () => {
    setElementParam(model, id, 'phaseAreas', areas.length ? areas : null)
    if (opts.clearSplits) setElementParam(model, id, 'phaseSplits', null)
  })
  return true
}

/** `null` on either field clears it; `undefined` leaves it as-is. */
export function setPhaseAreaTag(
  model: ModelDoc, id: ElementId, areaId: string,
  tag: { phaseCreated?: PhaseId | null; phaseDemolished?: PhaseId | null },
): boolean {
  const el = getElement(model, id)
  const existing = (el?.params.phaseAreas as PhaseAreaEntry[] | null | undefined) ?? []
  const idx = existing.findIndex((s) => s.id === areaId)
  if (idx < 0) return false
  const cur: PhaseAreaEntry = { ...existing[idx] }
  if (tag.phaseCreated === null) delete cur.phaseCreated
  else if (tag.phaseCreated !== undefined) cur.phaseCreated = tag.phaseCreated
  if (tag.phaseDemolished === null) delete cur.phaseDemolished
  else if (tag.phaseDemolished !== undefined) cur.phaseDemolished = tag.phaseDemolished
  const next = [...existing]
  next[idx] = cur
  setElementParam(model, id, 'phaseAreas', next)
  return true
}

export function removePhaseArea(model: ModelDoc, id: ElementId, areaId: string): boolean {
  const el = getElement(model, id)
  const existing = (el?.params.phaseAreas as PhaseAreaEntry[] | null | undefined) ?? []
  if (!existing.some((s) => s.id === areaId)) return false
  const next = existing.filter((s) => s.id !== areaId)
  setElementParam(model, id, 'phaseAreas', next.length ? next : null)
  return true
}

// ---------------------------------------------------------------------------
// MULTI-SELECT PROPERTIES round (2026-08-17, docs/MODELER.md): the properties
// palette edits a whole same-category selection at once. Each batch command is
// ONE transaction = one undo step (Yjs nests the per-element commands' own
// transactions into the caller's — the rotateSelection precedent), and returns
// the count actually applied so a stale id in the set degrades to a skip, never
// a failed batch.
// ---------------------------------------------------------------------------

/** Apply one param edit to every id — routes through applyParamEdit per element so
 *  compound semantics hold (a wall's locationLine re-designates without moving). */
export function applyParamEditToMany(model: ModelDoc, ids: ElementId[], key: string, value: ParamValue): number {
  let n = 0
  transact(model, () => {
    for (const id of ids) { if (applyParamEdit(model, id, key, value)) n++ }
  })
  return n
}

/** Set MULTIPLE instance params on every id (the door/window size dropdown sets
 *  widthFt + heightFt together — one pick, one undo step across the selection). */
export function setElementsParams(model: ModelDoc, ids: ElementId[], patch: Record<string, ParamValue>): number {
  let n = 0
  transact(model, () => {
    for (const id of ids) { if (setElementParams(model, id, patch)) n++ }
  })
  return n
}

/** Retype every id — guarded to elements whose category matches the type's own
 *  (a wall type means nothing to a door even if a mixed set sneaks through). */
export function setElementsType(model: ModelDoc, ids: ElementId[], typeId: TypeId): number {
  const type = getType(model, typeId)
  if (!type) return 0
  let n = 0
  transact(model, () => {
    for (const id of ids) {
      const em = maps(model).elements.get(id)
      if (!em || em.get('category') !== type.category) continue
      em.set('typeId', typeId)
      n++
    }
  })
  return n
}

export function setElementsLevel(model: ModelDoc, ids: ElementId[], levelId: LevelId): number {
  let n = 0
  transact(model, () => {
    for (const id of ids) { if (setElementLevel(model, id, levelId)) n++ }
  })
  return n
}

/** `phaseId: null` clears the field on every id (back to first-phase / untagged). */
export function setElementsPhaseCreated(model: ModelDoc, ids: ElementId[], phaseId: PhaseId | null): number {
  let n = 0
  transact(model, () => {
    for (const id of ids) { if (setElementPhaseCreated(model, id, phaseId)) n++ }
  })
  return n
}

/** `phaseId: null` clears the field on every id (never demolished). */
export function setElementsPhaseDemolished(model: ModelDoc, ids: ElementId[], phaseId: PhaseId | null): number {
  let n = 0
  transact(model, () => {
    for (const id of ids) { if (setElementPhaseDemolished(model, id, phaseId)) n++ }
  })
  return n
}

/** MODIFY PHASE-2: is this element pinned? (`pinned` is a bare universal instance
 *  param — never in a category schema, so it can't eyedropper-transfer, and
 *  `duplicateElement` strips it: pin protects THAT instance, not its identity.) */
export function elementPinned(model: ModelDoc, id: ElementId): boolean {
  return getElement(model, id)?.params.pinned === true
}

/** Pin/unpin a batch in one transaction (the armed Pin tool + the pet palette). */
export function setElementsPinned(model: ModelDoc, ids: ElementId[], pinned: boolean): void {
  const { elements } = maps(model)
  transact(model, () => {
    for (const id of ids) {
      const em = elements.get(id)
      if (!em) continue
      const pm = em.get('params') as Y.Map<unknown>
      pm.set('pinned', pinned)
    }
  })
}

/** Delete an element and (Phase-2-ready) everything hosted into it, in one transaction.
 *  MODIFY PHASE-2: a PINNED element refuses deletion (unpin first — the pin's whole
 *  job); its hosted children go down with an unpinned host regardless, since a hosted
 *  element cannot exist hostless. */
export function deleteElement(model: ModelDoc, id: ElementId): boolean {
  const { elements } = maps(model)
  if (!elements.has(id)) return false
  if (elementPinned(model, id)) return false
  const runId = runIdOf(model, id)
  transact(model, () => {
    const hostedIds: string[] = []
    elements.forEach((em, k) => { if (em.get('hostId') === id) hostedIds.push(k) })
    for (const h of hostedIds) elements.delete(h)
    // DINING CHAIRS BY COUNT: a table's chairs go with it
    if (isDiningTable(model, id)) for (const c of chairsOfTable(model, id)) elements.delete(c)
    // CASEWORK STUDIO stage B: a box's built-in appliances go with it
    const appl: string[] = []
    elements.forEach((em, k) => {
      const pm = em.get('params') as Y.Map<unknown> | undefined
      if (pm?.get('studioBoxId') === id && pm.get('studioRole') === 'appliance') appl.push(k)
    })
    for (const a of appl) elements.delete(a)
    elements.delete(id)
    // CASEWORK RUNS: the rest of the run closes the gap
    if (runId) healRunAfterDelete(model, runId)
  })
  return true
}

export function elementExists(model: ModelDoc, id: ElementId): boolean {
  return getElement(model, id) !== null
}

// ---------- parameter transfer (r19 — ArchiCAD eyedropper/syringe + Revit Create
// Similar). Schema-generic by construction: WHAT transfers is derived from the
// category's instance-param defs, so a new category or param participates
// automatically. Excluded: plan geometry (kind 'path2d'), computed values
// (`readonly`), and params marked `positional: true` (they encode WHERE the element
// sits — hostOffsetFt). hostId and levelId never transfer (placement, not identity).
// See docs/MODELER.md "Parameter transfer" before adding params or creation flows. ----------

export interface PickedSettings {
  category: CategoryId
  typeId: TypeId
  /** the transferable instance params the SOURCE stores itself (its own choices) */
  params: Record<string, ParamValue>
  /** transferable keys the source does NOT store — it follows its project / type / default for
   *  them, so the target is made to follow too (injection clears them) rather than freezing
   *  today's followed value (ROOF SETTINGS CLEANUP, 2026-10-08) */
  follow?: string[]
}

/** The instance-param keys that pick-up/inject move for a category. */
export function transferableParamKeys(category: CategoryId): string[] {
  const cat = getCategory(category)
  if (!cat) return []
  return schemaParams(cat.instanceParams)
    .filter((d) => d.kind !== 'path2d' && d.kind !== 'data' && !d.readonly && !d.positional)
    .map((d) => d.key)
}

/** params applyParamEdit routes through a compound command (see applyParamEdit) */
const ROUTED_PARAM_KEYS = new Set(['locationLine', 'chairCount', 'widthFt'])

/**
 * Eyedropper: read an element's transferable settings — the values it STORES itself, plus the list
 * of keys it FOLLOWS (unstored). The target gets the same type, so a followed key resolves the same
 * way on it; copying the resolved value instead would freeze today's project / default onto the
 * target and silently stop it following (ROOF SETTINGS CLEANUP, 2026-10-08 — Santiago's roofs had
 * every eave setting frozen this way, so the project's eave settings reached none of them).
 */
export function pickUpSettings(model: ModelDoc, id: ElementId): PickedSettings | null {
  const el = resolveElement(model, id)
  const rec = getElement(model, id)
  if (!el || !rec) return null
  const stored = (rec.params || {}) as Record<string, ParamValue>
  const params: Record<string, ParamValue> = {}
  const follow: string[] = []
  for (const key of transferableParamKeys(el.category)) {
    const v = stored[key]
    // keys whose WRITE re-shapes the element (a wall's location line re-designates the path, a
    // table's chair count regenerates chairs, a run box's width re-lays the run) keep the old
    // resolved-value copy — deleting them on the target would move or desync it
    const routed = ROUTED_PARAM_KEYS.has(key)
    if (v !== undefined && v !== null) params[key] = JSON.parse(JSON.stringify(v)) as ParamValue
    else if (routed && el.params[key] !== undefined && el.params[key] !== null) params[key] = JSON.parse(JSON.stringify(el.params[key])) as ParamValue
    else if (!routed) follow.push(key)
  }
  return { category: el.category, typeId: el.typeId, params, follow }
}

/**
 * Syringe: apply picked settings onto an existing element — type + every transferable
 * param, ONE transaction = one undo step. Same-category only (a wall's settings mean
 * nothing to a door). Param writes route through applyParamEdit so compound semantics
 * hold (a wall's locationLine re-designates without moving the body).
 */
export function injectSettings(model: ModelDoc, id: ElementId, picked: PickedSettings): boolean {
  const em = maps(model).elements.get(id)
  if (!em || em.get('category') !== picked.category) return false
  transact(model, () => {
    em.set('typeId', picked.typeId)
    for (const [key, value] of Object.entries(picked.params)) {
      applyParamEdit(model, id, key, value)
    }
    // the source follows these — so does the target
    const pm = em.get('params') as Y.Map<unknown>
    for (const key of picked.follow ?? []) if (pm.has(key)) pm.delete(key)
  })
  return true
}

// ---------------------------------------------------------------------------
// ROTATE + MIRROR — selection-aware batch entry points (docs/MODELER.md "ROTATE +
// MIRROR pet-palette commands"). Lives here, not in geometryOps.ts, because it needs
// duplicateElement (the "…a copy" variant) — geometryOps.ts stays free of that import to
// avoid a cycle (elements.ts already depends on geometryOps.ts via topology.ts).
// ---------------------------------------------------------------------------

/** Classify one selected id against the WHOLE selected set: a hosted element whose own
 *  host is ALSO in the set is skipped entirely — the host's own transform re-anchors it
 *  (corner-slider units follow for free: `cornerSliderLegFit` derives leg B live from
 *  both walls' current geometry, so mirroring both of a corner unit's hosts together
 *  "just works," and mirroring only one degrades to a stale-but-non-crashing fit rather
 *  than needing special-case code). A hosted element whose host is NOT in the set gets
 *  the standalone in-place flip (no axis to reflect across); everything else gets the
 *  real geometric transform. */
/** CEILING HOSTING round (2026-08-23): true for a hosted element with its OWN
 *  path geometry (recessedCan/pendant/flushMount) — it has real world position
 *  independent of its host, so a group transform must move it too, same as any
 *  freestanding element. False for door/window/wall-fixture (no path of their
 *  own — their position is DERIVED from the host, so it's correct to leave them
 *  to `applyRigidTransform`'s host-riding machinery instead). */
export function hasOwnPath(rec: { params?: Record<string, unknown> } | null): boolean {
  return !!(rec?.params?.path as Path2D | undefined)?.nodes?.length
}

function shouldSkipHostedWithinSelection(model: ModelDoc, id: ElementId, idSet: Set<ElementId>): boolean {
  const rec = getElement(model, id)
  return !!(rec?.hostId && idSet.has(rec.hostId) && !hasOwnPath(rec))
}

/** Rotate every id about `center` by `deg`, one transaction (Yjs nests transactions on
 *  the same doc into the caller's, so this is one undo step regardless of how many
 *  elements are involved). Hosted ids ride their host silently if it's also selected;
 *  a hosted id selected without its host is simply skipped (no rotate for hosted —
 *  "ride the host," and there's nothing here to rotate it ABOUT). MODIFY PHASE-2:
 *  `copy` duplicates each target first (the duplicate is born unpinned and becomes the
 *  result — the mirrorSelection convention); returns the ids actually transformed. */
export function rotateSelection(model: ModelDoc, ids0: ElementId[], center: Pt2, deg: number, copy = false): ElementId[] {
  // CASEWORK RUNS: a run transforms whole; DINING CHAIRS: a table carries its chairs;
  // CASEWORK STUDIO: a cabinet carries the sink / faucet set on it
  const ids = carriedByCasework(model, dropCarriedChairs(model, expandRuns(model, ids0)))
  const idSet = new Set(ids)
  const results: ElementId[] = []
  const pairs: [ElementId, ElementId][] = []
  transact(model, () => {
    for (const id of ids) {
      if (shouldSkipHostedWithinSelection(model, id, idSet)) continue
      let targetId = id
      if (copy) {
        const rec = getElement(model, id)
        // hosted without its host — nothing to rotate a copy of; a hosted
        // element with its OWN path (ceiling lights) still passes (CEILING
        // HOSTING round, 2026-08-23)
        if (!rec || (rec.hostId && !hasOwnPath(rec))) continue
        const nid = duplicateElement(model, id)
        if (!nid) continue
        targetId = nid
        pairs.push([id, nid])
      }
      if (rotateElement(model, targetId, center, deg, copy ? null : idSet)) results.push(targetId)
    }
    if (pairs.length) { remapCopiedRuns(model, pairs); relinkCopiedWallJoins(model, pairs); copyStudioFollowers(model, pairs) }
    if (!copy) reseatStudioFixtures(model, ids)
  })
  return results
}

/**
 * MODIFY PHASE-2 — Array (linear): `count` TOTAL positions including the originals, so
 * count 3 creates 2 copies at 1× and 2× `delta`. One transaction = one undo step for
 * the whole field of copies; each copy is born unpinned. Returns every created id.
 * MULTI MOVE/COPY FIX (2026-09-30): each step is a translateSelection COPY, so the
 * hosted rules match Copy exactly — doors selected with their wall repeat onto every
 * wall copy, doors selected alone repeat along their own wall.
 */
export function arraySelection(model: ModelDoc, ids: ElementId[], delta: Pt2, count: number): ElementId[] {
  const created: ElementId[] = []
  const n = Math.max(2, Math.floor(count))
  transact(model, () => {
    for (let k = 1; k < n; k++) {
      created.push(...translateSelection(model, ids, [delta[0] * k, delta[1] * k], true))
    }
  })
  return created
}

/**
 * Mirror every id across the axis, one transaction. `copy` duplicates each target FIRST
 * (matching "Move a copy": the original stays put, the duplicate becomes the result) —
 * hosted children are never copied along with their host (matches duplicateElement's
 * existing convention), so a "Mirror a copy" of a wall alone doesn't fork its doors.
 * Returns the ids that ended up transformed/created, for reselecting after a copy.
 */
/**
 * Translate every id by `delta`, one transaction (MODIFY TOOL FAMILY round — the batch
 * twin of geometryOps.translateElement, matching rotateSelection/mirrorSelection).
 * `copy` duplicates each target first; returns the ids that actually moved/were
 * created, for reselecting after a copy.
 *
 * MULTI MOVE/COPY FIX (2026-09-30, Matt: "I need to be able to move copy whatever I
 * have selected") — hosted elements with no path of their own (doors, windows, wall
 * fixtures) used to be dropped from a multi Move/Copy in two ways:
 *  - host ALSO selected: a Move still rides the host (hostOffsetFt is arc length,
 *    preserved by a rigid translate), but a COPY left the doors behind — the copied
 *    wall came out blank. Now each is duplicated onto its host's copy.
 *  - host NOT selected: skipped outright, so selecting several doors moved nothing.
 *    Now each slides along its own host by the delta's projection — exactly the
 *    single-element Move's along-host slide (HandleLayer), batched.
 * A wall copied ALONE still doesn't fork its doors (duplicateElement's convention).
 */
/**
 * CASEWORK STUDIO stage B: a copied cabinet takes the objects the Studio placed on it along —
 * its built-in appliances, sink, faucet and pot filler — re-seated on the copy. (Wall lights stay
 * behind: a copy may have no wall behind it, and a second light on the original's spot is worse
 * than none.)
 */
function copyStudioFollowers(model: ModelDoc, pairs: [ElementId, ElementId][]): void {
  const copyOf = new Map(pairs)
  const { elements } = maps(model)
  const followers = listElements(model).filter((e) => e.category === 'object' && typeof e.params.studioBoxId === 'string'
    && copyOf.has(e.params.studioBoxId as ElementId) && !copyOf.has(e.id))
  if (!followers.length) return
  for (const f of followers) {
    const nid = duplicateElement(model, f.id)
    const em = nid ? elements.get(nid) : null
    if (!em) continue
    ;(em.get('params') as Y.Map<unknown>).set('studioBoxId', copyOf.get(f.params.studioBoxId as ElementId)!)
    if (f.hostId && copyOf.has(f.hostId)) em.set('hostId', copyOf.get(f.hostId)!)
  }
  reseatStudioFixtures(model, [...copyOf.values()])
}

export function translateSelection(model: ModelDoc, ids0: ElementId[], delta: Pt2, copy = false,
  /** GROUP MOVE round 3: what the move had to do / couldn't do (geometryOps.newMoveReport) */
  report: MoveReport | null = null,
  /** STRETCH: elements reshaping in the same gesture — joints to them are left to the
   *  stretch, never dragged by this move */
  alsoMoving: Set<ElementId> | null = null): ElementId[] {
  // CASEWORK RUNS: a run moves whole; DINING CHAIRS: a table carries its chairs;
  // CASEWORK STUDIO: a cabinet carries the sink / faucet set on it
  const ids = carriedByCasework(model, dropCarriedChairs(model, expandRuns(model, ids0)))
  const idSet = new Set(ids)
  const movingSet = alsoMoving ? new Set([...ids, ...alsoMoving]) : idSet
  const results: ElementId[] = []
  const copyOf = new Map<ElementId, ElementId>()   // original → its copy (copy mode)
  const { elements } = maps(model)
  transact(model, () => {
    // pass 1: everything that carries its own geometry
    for (const id of ids) {
      const rec = getElement(model, id)
      if (!rec) continue
      if (rec.hostId && !hasOwnPath(rec)) continue   // hosted openings: pass 2
      let targetId = id
      if (copy) {
        const nid = duplicateElement(model, id)
        if (!nid) continue
        targetId = nid
        copyOf.set(id, nid)
      }
      // REVIT JOINS round: walls moving together keep the joints between them
      if (translateElement(model, targetId, delta, copy ? null : movingSet, report)) results.push(targetId)
    }
    if (copyOf.size) { remapCopiedRuns(model, [...copyOf.entries()]); relinkCopiedWallJoins(model, [...copyOf.entries()]); copyStudioFollowers(model, [...copyOf.entries()]) }
    // pass 2: hosted elements with no path of their own
    for (const id of ids) {
      const rec = getElement(model, id)
      if (!rec?.hostId || hasOwnPath(rec)) continue
      if (idSet.has(rec.hostId)) {
        if (!copy) continue   // rides its (moved) host
        const newHost = copyOf.get(rec.hostId)
        if (!newHost) continue
        const nid = duplicateElement(model, id)
        const em = nid ? elements.get(nid) : null
        if (!nid || !em) continue
        em.set('hostId', newHost)
        // a corner slider's second host follows its copy too, when it was copied
        const hostB = (typeof rec.params.hostBId === 'string' ? rec.params.hostBId : '') as ElementId
        if (hostB && copyOf.has(hostB)) (em.get('params') as Y.Map<unknown>).set('hostBId', copyOf.get(hostB)!)
        results.push(nid)
        continue
      }
      if (rec.params.pinned === true && !copy) continue
      const host = getElement(model, rec.hostId)
      const hostPath = host?.params.path as Path2D | undefined
      if (!hostPath?.nodes || hostPath.nodes.length < 2) continue
      const pts = flattenPathPts(hostPath)
      const s0 = typeof rec.params.hostOffsetFt === 'number' ? rec.params.hostOffsetFt : 0
      const p0 = polylinePointAt(pts, s0).pt
      const sNew = projectToPolyline(pts, [p0[0] + delta[0], p0[1] + delta[1]]).s
      let targetId: ElementId | null = id
      if (copy) targetId = duplicateElement(model, id)
      if (!targetId) continue
      setElementParam(model, targetId, 'hostOffsetFt', sNew)
      results.push(targetId)
    }
    if (!copy) reseatStudioFixtures(model, ids)
  })
  return results
}

export function mirrorSelection(model: ModelDoc, ids0: ElementId[], axisOrigin: Pt2, axisDir: Pt2, copy = false): ElementId[] {
  // CASEWORK RUNS: a run mirrors whole; DINING CHAIRS: a table carries its chairs;
  // CASEWORK STUDIO: a cabinet carries the sink / faucet set on it
  const ids = carriedByCasework(model, dropCarriedChairs(model, expandRuns(model, ids0)))
  const idSet = new Set(ids)
  const results: ElementId[] = []
  const pairs: [ElementId, ElementId][] = []
  transact(model, () => {
    for (const id of ids) {
      if (shouldSkipHostedWithinSelection(model, id, idSet)) continue
      const rec = getElement(model, id)
      if (!rec) continue
      let targetId = id
      if (copy) {
        const nid = duplicateElement(model, id)
        if (!nid) continue
        targetId = nid
        pairs.push([id, nid])
      }
      // CEILING HOSTING round (2026-08-23): a hosted element with its OWN path
      // has real geometry to reflect, unlike a wall fixture/door/window (no
      // path of their own — "in place" hand-flip is their only meaningful
      // mirror). Gate on path presence, not hostId alone.
      if (rec.hostId && !hasOwnPath(rec)) mirrorHostedInPlace(model, targetId)
      else mirrorElement(model, targetId, axisOrigin, axisDir, copy ? null : idSet)
      results.push(targetId)
    }
    if (pairs.length) { remapCopiedRuns(model, pairs); relinkCopiedWallJoins(model, pairs); copyStudioFollowers(model, pairs) }
    if (!copy) reseatStudioFixtures(model, ids)
  })
  return results
}

/** STRETCH: what one stretch did (the command line says it, never a silent no-op) */
export interface StretchReport { stretched: number; moved: number; pinned: number; refused: number }

/**
 * STRETCH (2026-10-23, Matt — ArchiCAD's Stretch, AutoCAD's STRETCH for the model): a
 * crossing window `rect` ([minX, minY, maxX, maxY], plan feet) and a displacement. Among
 * `ids` (the candidates — the plan's level):
 *  - an element whose every point is inside the window MOVES whole (translateSelection —
 *    hosted doors ride it, a casework run moves whole, a table carries its chairs);
 *  - an element with only SOME points inside STRETCHES (stretchElementPath) — a wall end,
 *    one side of a floor/roof/ceiling, a railing or beam end;
 *  - a door/window (no geometry of its own) whose anchor is inside the window but whose
 *    wall isn't moving whole slides along it with the stretch.
 * Partial stretches are computed from each element's ORIGINAL points, and the whole moves
 * never drag a stretching partner, so the order can't skew the result. One undo step.
 */
export function stretchElements(
  model: ModelDoc, ids0: ElementId[], rect: [number, number, number, number], delta: Pt2,
): StretchReport {
  const out: StretchReport = { stretched: 0, moved: 0, pinned: 0, refused: 0 }
  if (Math.hypot(delta[0], delta[1]) < 1e-9) return out
  const inside = (p: Pt2) => p[0] >= rect[0] && p[0] <= rect[2] && p[1] >= rect[1] && p[1] <= rect[3]
  const whole: ElementId[] = []
  const partial: ElementId[] = []
  const hosted: ElementId[] = []
  const ptsOf = (params: Record<string, unknown>): Pt2[] => {
    const res: Pt2[] = []
    for (const k of ['path', 'boundary']) {
      const p = params[k] as Path2D | undefined
      if (p?.nodes?.length) res.push(...p.nodes.map((n) => n.pt))
    }
    for (const k of ['boundaryHoles', 'verticalCuts']) {
      const v = params[k] as Path2D[] | undefined
      if (Array.isArray(v)) for (const p of v) if (p?.nodes?.length) res.push(...p.nodes.map((n) => n.pt))
    }
    return res
  }
  for (const id of new Set(ids0)) {
    const rec = getElement(model, id)
    if (!rec || rec.category === 'body') continue
    if (rec.hostId && !hasOwnPath(rec)) { hosted.push(id); continue }
    const pts = ptsOf(rec.params)
    if (!pts.length) continue
    const n = pts.filter(inside).length
    if (!n) continue
    ;(n === pts.length ? whole : partial).push(id)
  }
  const wholeSet = new Set(whole)
  const partialSet = new Set(partial)
  // a door/window inside the window whose wall is stretching travels with that stretch
  // (stretchElementPath); one on a wall that isn't moving at all slides along it
  const slide = hosted.filter((id) => {
    const rec = getElement(model, id)!
    if (wholeSet.has(rec.hostId!) || partialSet.has(rec.hostId!)) return false
    const host = getElement(model, rec.hostId!)
    const hp = host?.params.path as Path2D | undefined
    if (!hp?.nodes || hp.nodes.length < 2) return false
    const s = typeof rec.params.hostOffsetFt === 'number' ? rec.params.hostOffsetFt : 0
    return inside(polylinePointAt(flattenPathPts(hp), s).pt)
  })
  transact(model, () => {
    if (whole.length || slide.length) {
      const pinnedBefore = whole.filter((id) => elementPinned(model, id)).length
      translateSelection(model, [...whole, ...slide], delta, false, null, partialSet)
      out.moved += whole.length - pinnedBefore + slide.length
      out.pinned += pinnedBefore
    }
    for (const id of partial) {
      const r = stretchElementPath(model, id, inside, delta)
      if (r === 'stretched') out.stretched++
      else if (r === 'pinned') out.pinned++
      else if (r === 'refused') out.refused++
    }
  })
  return out
}

/**
 * DISTRIBUTE round: translate each id by its OWN delta (unlike translateSelection's
 * one shared delta) — one transaction, one undo step. The distribute algorithm itself
 * (anchor derivation, axis projection, spacing) lives in the frontend editing layer
 * (frontend/src/bim/editing/distribute.js, and the Align family's
 * frontend/src/bim/editing/align.js) since it depends on the plan-pick body registry
 * (pickConfigFor) — this is just the generic "apply N different deltas atomically"
 * primitive.
 *
 * Hosted ids are skipped UNLESS they carry their own path (`hasOwnPath` — the CEILING
 * HOSTING round's convention already applied to translateSelection/mirrorSelection
 * above). A door/window/wall-fixture has no independent geometry — its position is
 * derived from the host — so it correctly rides the host if it's also in the batch.
 * A ceiling-hosted light (recessedCan/pendant/flushMount) DOES have its own path/
 * rotationDeg; it was wrongly caught by a blanket `rec.hostId` check here until this
 * fix (2026-08-23's CEILING HOSTING round updated every OTHER hosted-skip site in
 * this file but missed this one, silently regressing Distribute/Align for lights —
 * and for any other selection that dropped below the eligible-count minimum once
 * those lights were miscounted as excluded).
 */
export function translateEach(model: ModelDoc, moves: { id: ElementId; delta: Pt2 }[]): ElementId[] {
  const results: ElementId[] = []
  const moving = new Set(moves.map((m) => m.id))
  const runsMoved = new Set<string>()
  transact(model, () => {
    for (const { id, delta } of moves) {
      const rec = getElement(model, id)
      if (!rec) continue
      if (rec.hostId && !hasOwnPath(rec)) continue
      // CASEWORK AS ONE ITEM (2026-10-09): a box never leaves its line — the whole casework
      // line moves, once, by the first delta given for any of its boxes
      const runId = runIdOf(model, id)
      if (runId) {
        if (runsMoved.has(runId)) continue
        runsMoved.add(runId)
        results.push(...translateSelection(model, [id], delta))
        continue
      }
      if (translateElement(model, id, delta, moving)) results.push(id)
    }
  })
  return results
}

/** SCOPE ON THE ELEMENT (scope.ts): set (or clear, null) an element's remodel scope — one undo step */
export function setElementScope(model: ModelDoc, ids: ElementId[], scope: ElementScope | null): number {
  let n = 0
  transact(model, () => {
    for (const id of ids) {
      const pm = maps(model).elements.get(id)?.get('params') as Y.Map<unknown> | undefined
      if (!pm) continue
      const s = scope ? normalizeScope(scope) : null
      if (s && scopeHasWork(s)) pm.set(SCOPE_KEY, s)
      else pm.delete(SCOPE_KEY)
      n++
    }
  })
  return n
}

/**
 * DEMOLISH LAYERS (2026-10-14): set the verb of some of a wall's parts in a phase, from the
 * demolish tool (a click marks, a marker undoes or puts back the same). `assembly` is the wall's
 * RESOLVED assembly (painted finishes included): legacy positional keys are rewritten against it
 * on the way through. `verb` null = keep (the mark comes off). A wall's scope has one phase:
 * marking in another phase while marks stand is refused ('otherPhase'). One undo step.
 */
export function setWallScopeParts(
  model: ModelDoc, wallId: ElementId, phase: string,
  assembly: { layers: { function: string; materialId: string }[]; coreBoundary?: { start: number; end: number } } | null | undefined,
  keys: string[], verb: ScopeVerb | null,
): 'ok' | 'otherPhase' | 'missing' {
  const pm = maps(model).elements.get(wallId)?.get('params') as Y.Map<unknown> | undefined
  if (!pm) return 'missing'
  const cur = migrateWallScope(normalizeScope(pm.get(SCOPE_KEY)), assembly)
  if (cur && cur.phase !== phase && scopeHasWork(cur)) return 'otherPhase'
  const parts: ElementScope['parts'] = { ...(cur?.parts ?? {}) }
  for (const k of keys) {
    if (!verb || verb === 'keep') delete parts[k]
    else parts[k] = { verb }
  }
  const next: ElementScope = { phase, parts, ...(cur?.add ? { add: cur.add } : {}), ...(cur?.planes ? { planes: cur.planes } : {}) }
  transact(model, () => {
    if (scopeHasWork(next)) pm.set(SCOPE_KEY, next)
    else pm.delete(SCOPE_KEY)
  })
  return 'ok'
}

/**
 * ROOF LAYERS BY PLANE (2026-11-02): set a roof's top layers (ROOF_PLANE_PARTS) on some of its
 * planes, from the demolish tool (a click marks the plane under it, a marker undoes or puts back
 * the same). `verb` null = keep: the plane's own mark comes off, and where the whole roof is
 * marked (the Scope panel) the plane holds an explicit keep. Same one-phase rule. One undo step.
 */
export function setRoofPlaneScopeParts(
  model: ModelDoc, roofId: ElementId, phase: string, nodeIds: string[], keys: string[], verb: ScopeVerb | null,
): 'ok' | 'otherPhase' | 'missing' {
  const pm = maps(model).elements.get(roofId)?.get('params') as Y.Map<unknown> | undefined
  if (!pm) return 'missing'
  const cur = normalizeScope(pm.get(SCOPE_KEY))
  if (cur && cur.phase !== phase && scopeHasWork(cur)) return 'otherPhase'
  const planes: NonNullable<ElementScope['planes']> = {}
  for (const [id, ps] of Object.entries(cur?.planes ?? {})) planes[id] = { ...ps }
  for (const id of nodeIds) {
    const ps = { ...(planes[id] ?? {}) }
    for (const k of keys) {
      const whole = cur?.parts[k]?.verb ?? 'keep'
      const want = verb ?? 'keep'
      if (want === whole) delete ps[k]
      else ps[k] = { verb: want }
    }
    if (Object.keys(ps).length) planes[id] = ps
    else delete planes[id]
  }
  // new roofing troweled onto a plane only stands while that plane's roofing is off: putting the
  // roofing back takes the new roofing off too
  const add = { ...(cur?.add ?? {}) }
  if (add.roofing) {
    const rf = { ...add.roofing }
    for (const id of nodeIds) if ((planes[id]?.roofing?.verb ?? cur?.parts.roofing?.verb ?? 'keep') !== 'remove') delete rf[id]
    if (Object.keys(rf).length) add.roofing = rf
    else delete add.roofing
  }
  const next: ElementScope = {
    phase, parts: { ...(cur?.parts ?? {}) },
    ...(Object.keys(add).length ? { add } : {}), ...(Object.keys(planes).length ? { planes } : {}),
  }
  transact(model, () => {
    if (scopeHasWork(next)) pm.set(SCOPE_KEY, next)
    else pm.delete(SCOPE_KEY)
  })
  return 'ok'
}

/**
 * ROOF LAYERS (2026-10-30): the trowel on an existing roof in a later phase records the new
 * roofing per plane in the roof's scope (`scope.add.roofing`) instead of repainting the existing
 * roofing. `nodeIds` null = every plane; `materialId` null takes the new roofing back off those
 * planes. Same one-phase rule as the marks. One undo step.
 */
export function setRoofScopeAdd(
  model: ModelDoc, roofId: ElementId, phase: string, nodeIds: string[] | null, materialId: string | null,
): 'ok' | 'otherPhase' | 'missing' | 'same' {
  const pm = maps(model).elements.get(roofId)?.get('params') as Y.Map<unknown> | undefined
  if (!pm) return 'missing'
  const cur = normalizeScope(pm.get(SCOPE_KEY))
  if (cur && cur.phase !== phase && scopeHasWork(cur)) return 'otherPhase'
  const all = ((pm.get('boundary') as { nodes?: { id?: string }[] } | null)?.nodes ?? []).map((n) => n.id).filter((x): x is string => !!x)
  const ids = nodeIds ?? all
  const roofing = { ...(cur?.add?.roofing ?? {}) }
  let changed = false
  for (const id of ids) {
    if ((roofing[id] ?? null) === (materialId || null)) continue
    changed = true
    if (materialId) roofing[id] = materialId
    else delete roofing[id]
  }
  if (!changed) return 'same'
  const add = { ...(cur?.add ?? {}) }
  if (Object.keys(roofing).length) add.roofing = roofing
  else delete add.roofing
  const next: ElementScope = { phase, parts: { ...(cur?.parts ?? {}) }, ...(Object.keys(add).length ? { add } : {}), ...(cur?.planes ? { planes: cur.planes } : {}) }
  transact(model, () => {
    if (scopeHasWork(next)) pm.set(SCOPE_KEY, next)
    else pm.delete(SCOPE_KEY)
  })
  return 'ok'
}

/**
 * NEW FINISH IN A LATER PHASE (2026-10-14): the trowel on an existing wall's face in a phase after
 * the wall's own records the new finish in the wall's scope (`scope.add`) instead of overwriting
 * the existing finish. `materialId` null takes it back off. Same one-phase rule as the marks.
 */
export function setWallScopeAdd(
  model: ModelDoc, wallId: ElementId, phase: string,
  assembly: { layers: { function: string; materialId: string }[]; coreBoundary?: { start: number; end: number } } | null | undefined,
  side: 'exterior' | 'interior', materialId: string | null,
): 'ok' | 'otherPhase' | 'missing' | 'same' {
  const pm = maps(model).elements.get(wallId)?.get('params') as Y.Map<unknown> | undefined
  if (!pm) return 'missing'
  const cur = migrateWallScope(normalizeScope(pm.get(SCOPE_KEY)), assembly)
  if (cur && cur.phase !== phase && scopeHasWork(cur)) return 'otherPhase'
  if ((cur?.add?.[side] ?? null) === (materialId || null)) return 'same'
  const add = { ...(cur?.add ?? {}) }
  if (materialId) add[side] = materialId
  else delete add[side]
  const next: ElementScope = { phase, parts: { ...(cur?.parts ?? {}) }, ...(Object.keys(add).length ? { add } : {}), ...(cur?.planes ? { planes: cur.planes } : {}) }
  transact(model, () => {
    if (scopeHasWork(next)) pm.set(SCOPE_KEY, next)
    else pm.delete(SCOPE_KEY)
  })
  return 'ok'
}
